import { Box, Typography, InputAdornment } from "@mui/material";
import { memo, useCallback, useMemo } from "react";
import { IVariableFieldProps } from "./models";
import { TextField } from "../../../../TextField/TextField";
import { useTranslation } from "react-i18next";
import { useFormContext } from "@4uhub/lib4uhub";
import { useWatch } from "react-hook-form";
import { TWhatsappTemplateForm } from "../../../../../../screens/WhatsappTemplate/WhatsappTemplateForm/whatsappTemplateSchema";
import { useTemplateWhats } from "../../../../../../store/contexts/TemplateWhatsContext";

const translationPath = "components.variableField.";

type Props = IVariableFieldProps & {
  characterIndex?: number;
  editable?: boolean;
};

export const VariableField = memo(
  ({ variable, index, labelWidth, characterIndex, editable = true }: Props) => {
    const { t } = useTranslation();

    const {
      control,
      formState: { errors },
      getValues,
    } = useFormContext<TWhatsappTemplateForm>();

    const { fetchVariables } = useTemplateWhats();

    const idx = characterIndex ?? index;

    const exampleValue: string = useWatch({
      name: `bodyExamples.${index}.example`,
      control,
    });

    const characterLimitRaw: string | undefined = useWatch({
      name: `characterLimitRequests.${idx}.characterLimit`,
      control,
    });

    const maxLength: number | undefined = useMemo(() => {
      if (characterLimitRaw == null || characterLimitRaw === "")
        return undefined;
      const parsed = parseInt(
        String(characterLimitRaw).replace(/\D+/g, ""),
        10,
      );
      return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
    }, [characterLimitRaw]);

    const totalCharacters = (exampleValue ?? "").length;
    const exceeded = !!maxLength && totalCharacters > maxLength;

    const isEmpty = (exampleValue ?? "").trim().length === 0;

    const exampleSchemaErrorMsg = ((errors?.bodyExamples?.[index] as any)
      ?.example?.message ?? undefined) as string | undefined;

    const charLimitSchemaErrorMsg = ((
      errors?.characterLimitRequests?.[idx] as any
    )?.characterLimit?.message ?? undefined) as string | undefined;

    const exampleHasError = Boolean(
      isEmpty || exceeded || exampleSchemaErrorMsg,
    );

    let exampleHelperText = "";
    if (exampleSchemaErrorMsg) {
      exampleHelperText = t(exampleSchemaErrorMsg);
    } else if (exceeded && maxLength !== undefined) {
      exampleHelperText = t(translationPath + "exceeded", {
        max: maxLength,
      });
    } else {
      exampleHelperText = "";
    }

    const charLimitHasError = Boolean(charLimitSchemaErrorMsg);
    const charLimitHelperText = charLimitSchemaErrorMsg
      ? t(charLimitSchemaErrorMsg)
      : "";

    const handleBodyExamplesChange = useCallback(() => {
      const bodyExamples = getValues("bodyExamples");

      if (bodyExamples) {
        fetchVariables(bodyExamples.map((v) => v.example));
      }
    }, [fetchVariables, getValues]);

    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box minWidth={labelWidth} mr={2}>
          <Typography>{variable}</Typography>
        </Box>

        <TextField
          label={`${t(translationPath + "label")} ${variable}`}
          name={`bodyExamples.${index}.example`}
          size="small"
          fullWidth
          error={exampleHasError}
          helperText={exampleHelperText}
          onChangeValue={handleBodyExamplesChange}
          InputProps={{
            endAdornment: (
              <InputAdornment
                position="end"
                sx={{ marginBottom: 2, alignSelf: "end" }}
              >
                <Typography
                  variant="body2"
                  sx={(theme) => ({
                    color: exampleHasError
                      ? theme.palette.error.main
                      : theme.palette.grey[
                          theme.palette.mode === "light" ? 400 : 600
                        ],
                  })}
                >
                  {maxLength
                    ? `${totalCharacters}/${maxLength}`
                    : `${totalCharacters}`}
                </Typography>
              </InputAdornment>
            ),
          }}
          sx={(t) => ({
            background: `linear-gradient(${t.palette.background.default} 40px, transparent 40px)`,
            borderRadius: 1,
          })}
        />

        <TextField
          label={t(translationPath + "character_limit") as string}
          name={`characterLimitRequests.${idx}.characterLimit`}
          size="small"
          fullWidth
          disabled={!editable}
          error={charLimitHasError}
          helperText={charLimitHelperText}
          inputProps={{
            inputMode: "numeric",
            pattern: "[0-9]*",
            min: 1,
          }}
          sx={(t) => ({
            background: `linear-gradient(${t.palette.background.default} 40px, transparent 40px)`,
            borderRadius: 1,
            opacity: editable ? 1 : 0.6,
          })}
        />
      </Box>
    );
  },
);
